import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

/**
 * Default radius (metres) applied to a manually-added pin. Kept here as the
 * shared constant even though this context no longer holds a radius itself —
 * `useComputePreferences` imports it as its own fallback default.
 */
export const DEFAULT_RADIUS_METERS = 1000;

/** A geographic coordinate pair. */
export interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * A location add that kicked off an asynchronous backend computation. Tracked
 * at the provider level so it survives the tool panel unmounting and so
 * several runs can be in flight at once (each add is independent — a new one
 * is never blocked by a previous run still computing).
 */
export interface ComputeRun {
  /** Project id returned by the 202 `POST …/locations`; keys the SSE stream. */
  projectId: string;
  /** Epoch ms stamped on register; a persisted run older than the backend's compute timeout is stale. */
  launchedAt: number;
  /** Workspace the run targets — captured so a workspace switch can't corrupt
   *  the wrong cache when the run terminates. */
  workspaceId: string;
  /** Optimistic marker ids written during the add; rolled back on FAILED. */
  tempIds: string[];
  /** Whether a single-pin add should auto-select on COMPLETED. */
  autoSelectSingle: boolean;
  /** Launch coordinates for the single-pin case, used to resolve the real backend id on COMPLETED. */
  coordinates?: Coordinates;
  /**
   * Ids of the workspace's markers that already existed right before this run
   * launched. Coordinates are not unique to one location — recomputing at the
   * same address reuses the shared `coordinates` row but creates a brand new
   * location id — so matching the real id by coordinates alone can resolve to
   * a pre-existing marker instead of the one this run just created. Excluding
   * these ids from the coordinate match on COMPLETED disambiguates the two.
   */
  existingMarkerIds: string[];
}

/** Read-only manual-pin state. */
export interface ManualPinState {
  armedToolId: string | null;
  /** Compute runs currently being followed over SSE. */
  activeComputeRuns: ComputeRun[];
  /**
   * TempIds whose launch mutation is in flight but not yet registered as a
   * `ComputeRun` (the backend hasn't returned a project id yet). A location
   * picked via `selectLocation(tempId)` right at launch must be treated as
   * "computing" from this instant — not only once `registerComputeRun` runs —
   * otherwise the results query briefly enables for a tempId that doesn't
   * exist as a real backend location yet, 404s, exhausts retries, and its
   * error handler clears the selection out from under the just-opened panel.
   */
  pendingTempIds: string[];
}

/** Full manual-pin context API: state plus the actions that mutate it. */
export interface ManualPinApi extends ManualPinState {
  arm(toolId: string): void;
  disarm(): void;
  /** Mark tempIds as launching, before the backend has assigned a project id. */
  markPending(tempIds: string[]): void;
  /** Drop pending tempIds whose launch mutation failed (never became a run). */
  clearPending(tempIds: string[]): void;
  /** Register a compute run so the surface starts following it over SSE. */
  registerComputeRun(run: Omit<ComputeRun, 'launchedAt'>): void;
  /** Drop a finished run by project id once its SSE terminal has been handled. */
  clearComputeRun(projectId: string): void;
}

type ManualPinAction =
  | { type: 'ARM'; toolId: string }
  | { type: 'DISARM' }
  | { type: 'MARK_PENDING'; tempIds: string[] }
  | { type: 'CLEAR_PENDING'; tempIds: string[] }
  | { type: 'REGISTER_COMPUTE_RUN'; run: Omit<ComputeRun, 'launchedAt'> }
  | { type: 'CLEAR_COMPUTE_RUN'; projectId: string };

const INITIAL_STATE: ManualPinState = {
  armedToolId: null,
  activeComputeRuns: [],
  pendingTempIds: [],
};

/** Pure, fully-immutable reducer for the manual-pin state. */
function manualPinReducer(state: ManualPinState, action: ManualPinAction): ManualPinState {
  switch (action.type) {
    case 'ARM':
      return { ...state, armedToolId: action.toolId };

    case 'DISARM':
      return { ...state, armedToolId: null };

    case 'MARK_PENDING':
      return {
        ...state,
        pendingTempIds: [...new Set([...state.pendingTempIds, ...action.tempIds])],
      };

    case 'CLEAR_PENDING':
      return {
        ...state,
        pendingTempIds: state.pendingTempIds.filter((id) => !action.tempIds.includes(id)),
      };

    case 'REGISTER_COMPUTE_RUN':
      // Ignore a duplicate project id so a re-render can't double-subscribe.
      if (state.activeComputeRuns.some((run) => run.projectId === action.run.projectId)) {
        return state;
      }
      return {
        ...state,
        activeComputeRuns: [...state.activeComputeRuns, { ...action.run, launchedAt: Date.now() }],
        // The run's tempIds are now tracked via activeComputeRuns; drop them
        // from pendingTempIds so the set doesn't grow unbounded.
        pendingTempIds: state.pendingTempIds.filter((id) => !action.run.tempIds.includes(id)),
      };

    case 'CLEAR_COMPUTE_RUN':
      return {
        ...state,
        activeComputeRuns: state.activeComputeRuns.filter(
          (run) => run.projectId !== action.projectId,
        ),
      };

    default:
      return state;
  }
}

const ManualPinContext = createContext<ManualPinApi | null>(null);

/**
 * `activeComputeRuns` is mirrored to localStorage so an in-flight run
 * survives a page refresh or a new tab — otherwise the SSE subscription
 * (and the progress ring / computing indicator it drives) is silently lost
 * on reload even though the backend job is still running, unlike the
 * CSV-batch-upload flow's history row, which re-derives its running state
 * from a backend query on every mount. `armedToolId` is intentionally NOT
 * persisted — re-arming the tool after a reload is a one-click action and a
 * stale "armed" state surviving a reload would be surprising.
 */
const ACTIVE_COMPUTE_RUNS_STORAGE_KEY = 'spot-on:active-compute-runs';

/** Matches the compute workflow's WorkflowExecutionTimeout: past it the backend has given up too. */
export const COMPUTE_RUN_MAX_AGE_MS = 30 * 60 * 1000;

function loadPersistedComputeRuns(): ComputeRun[] {
  try {
    const raw = localStorage.getItem(ACTIVE_COMPUTE_RUNS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const freshSince = Date.now() - COMPUTE_RUN_MAX_AGE_MS;
    return parsed.filter(
      (run: Partial<ComputeRun>) =>
        typeof run.launchedAt === 'number' && run.launchedAt > freshSince,
    );
  } catch {
    // Corrupt/foreign JSON under our key — start clean rather than crash.
    return [];
  }
}

function persistComputeRuns(runs: ComputeRun[]): void {
  try {
    if (runs.length === 0) {
      localStorage.removeItem(ACTIVE_COMPUTE_RUNS_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_COMPUTE_RUNS_STORAGE_KEY, JSON.stringify(runs));
    }
  } catch {
    // Storage full or unavailable (e.g. Safari private mode) — the run still
    // works for this session, it just won't survive a refresh.
  }
}

/**
 * Single source of truth for the armed-tool state and the in-flight compute
 * runs shared by the map-click and address-search entry points.
 */
export function ManualPinProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(manualPinReducer, INITIAL_STATE, (initial) => ({
    ...initial,
    activeComputeRuns: loadPersistedComputeRuns(),
  }));

  // Re-persist on every change, including the run being cleared (removes the
  // stored key once the list is empty again).
  useEffect(() => {
    persistComputeRuns(state.activeComputeRuns);
  }, [state.activeComputeRuns]);

  const arm = useCallback((toolId: string) => {
    dispatch({ type: 'ARM', toolId });
  }, []);

  const disarm = useCallback(() => {
    dispatch({ type: 'DISARM' });
  }, []);

  const markPending = useCallback((tempIds: string[]) => {
    dispatch({ type: 'MARK_PENDING', tempIds });
  }, []);

  const clearPending = useCallback((tempIds: string[]) => {
    dispatch({ type: 'CLEAR_PENDING', tempIds });
  }, []);

  const registerComputeRun = useCallback((run: Omit<ComputeRun, 'launchedAt'>) => {
    dispatch({ type: 'REGISTER_COMPUTE_RUN', run });
  }, []);

  const clearComputeRun = useCallback((projectId: string) => {
    dispatch({ type: 'CLEAR_COMPUTE_RUN', projectId });
  }, []);

  const value = useMemo<ManualPinApi>(
    () => ({
      ...state,
      arm,
      disarm,
      markPending,
      clearPending,
      registerComputeRun,
      clearComputeRun,
    }),
    [state, arm, disarm, markPending, clearPending, registerComputeRun, clearComputeRun],
  );

  return <ManualPinContext.Provider value={value}>{children}</ManualPinContext.Provider>;
}

/**
 * Access the manual-pin context.
 *
 * @throws If called outside a {@link ManualPinProvider}.
 */
export function useManualPin(): ManualPinApi {
  const context = useContext(ManualPinContext);
  if (context === null) {
    throw new Error('useManualPin must be used within a ManualPinProvider');
  }
  return context;
}
